import React, { useState, useRef, useCallback, type ChangeEvent } from 'react'
import InputAdornment from '@mui/material/InputAdornment'
import { SearchBar } from './styles'
import { useTheme } from '@mui/material/styles'
import { Box, useMediaQuery } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CustomTooltip from '../CustomTooltip'
import { useAppSearchParams } from '../../hooks/useAppSearchParams'
import { Search as SearchIcon } from 'lucide-react'
import { grey, shade } from '../../colors'

interface SearchProps {
  onSearch?: (value: string) => void
  placeholder?: string
  fillContainer?: boolean
  stayFocusOnEnter?: boolean
  onChange?: (value: string) => void
  disabled?: boolean
  showClearIcon?: boolean
}

const Search: React.FC<SearchProps> = ({
  onSearch,
  placeholder = 'Search',
  fillContainer = false,
  stayFocusOnEnter = false,
  onChange,
  disabled,
  showClearIcon = true
}) => {
  const theme = useTheme()
  const { removeUrlParams } = useAppSearchParams()

  const [searchParams] = useSearchParams()
  const initialSearch = searchParams.get('search') || ''

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [prevValue, setPrevValue] = useState<string>(initialSearch)
  const [searchValue, setSearchValue] = useState<string>(initialSearch)
  const inputRef = useRef<HTMLInputElement>(null)

  const removeSearchParam = useCallback(() => {
    removeUrlParams('search')
  }, [removeUrlParams])

  const handleOnChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value ?? ''
    if (value !== searchValue) {
      setSearchValue(value)
      onChange?.(value)
    }
  }, [onChange, searchValue])

  const handleClear = useCallback((): void => {
    setSearchValue('')
    removeSearchParam()
    if (prevValue !== '') {
      onSearch?.('')
      setPrevValue('')
    }
    inputRef.current?.focus()
  }, [onChange, onSearch, isMobile, searchValue, removeSearchParam])

  const onSearchClick = useCallback((): void => {
    if (searchValue !== prevValue) {
      onSearch?.(searchValue)
      setPrevValue(searchValue)
      removeSearchParam()
    }
  }, [onSearch, searchValue, prevValue])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      if (!stayFocusOnEnter) {
        inputRef.current?.blur()
      } else {
        inputRef.current?.focus()
      }
      if (searchValue !== prevValue && searchValue) {
        onSearch?.(searchValue)
        setPrevValue(searchValue)
        removeSearchParam()
      }
    }
  }, [stayFocusOnEnter, onSearch, searchValue, prevValue, removeSearchParam])

  return (
    <SearchBar
      fillContainer={fillContainer}
      fullWidth
      variant="outlined"
      placeholder={placeholder}
      isMobile={isMobile}
      onChange={handleOnChange}
      value={searchValue}
      onKeyDown={handleKeyDown}
      inputRef={inputRef}
      disabled={disabled}
      InputProps={{
        autoComplete: 'off',
        endAdornment:
          <Box display='flex' alignItems='center' gap={1} >
            {searchValue && showClearIcon &&
              <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                <CustomTooltip title={prevValue !== '' && 'Clear & Refetch'} placement="top">
                  <CloseRoundedIcon onClick={handleClear} style={{ cursor: 'pointer', fontSize: '18px' }} />
                </CustomTooltip>
              </InputAdornment>
            }
            <InputAdornment position="start">
              <IconButton
                onClick={onSearchClick}
                disableRipple
                sx={{
                  color: grey[950],
                  backgroundColor: grey[0],
                  borderLeft: `1px solid ${grey[350]}`,
                  boxShadow: '0px 1px 2px 0px rgba(32, 41, 57, 0.05)',
                  marginRight: -2,
                  borderRadius: theme => theme.spacing(0, 2, 2, 0),
                  '&:hover': {
                    color: grey[700],
                    backgroundColor: grey[750]
                  }

                }}
              >
                <SearchIcon size={20} />
              </IconButton>
            </InputAdornment>
          </Box>

      }}
    />
  )
}

export default Search
